import { Component } from "react";
import { useRouteError } from "react-router-dom";
import { FaExclamationTriangle } from "react-icons/fa";

import "./errorBoundary.scss";


/*
 * Global frontend (render/runtime) error handling.
 *
 * Two entry points share one branded fallback:
 *  - <ErrorBoundary> (class) wraps the app as a top-level safety net for render
 *    errors thrown outside the data router.
 *  - <RouteErrorElement /> is used as the data router's `errorElement`, which is
 *    what actually catches render errors inside routed pages (createBrowserRouter
 *    intercepts those before any outer class boundary would see them).
 *
 * Both offer a hard reload / go-home so a corrupted render can't trap the user.
 */

function ErrorFallback({ error }) {
  return (
    <div className="AppErrorBoundary">
      <div className="AppErrorBoundary_card" dir="rtl">
        <span className="AppErrorBoundary_icon">
          <FaExclamationTriangle />
        </span>
        <span className="AppErrorBoundary_title">حدث خطأ ما</span>
        <span className="AppErrorBoundary_desc">
          واجه التطبيق مشكلة غير متوقعة. يمكنك إعادة تحميل الصفحة أو العودة إلى الصفحة الرئيسية.
        </span>
        <div className="AppErrorBoundary_actions">
          <button
            type="button"
            className="AppErrorBoundary_btn"
            onClick={() => window.location.reload()}
          >
            إعادة تحميل الصفحة
          </button>
          <button
            type="button"
            className="AppErrorBoundary_btn AppErrorBoundary_btn_ghost"
            onClick={() => window.location.assign("/emp")}
          >
            الصفحة الرئيسية
          </button>
        </div>

        {process.env.NODE_ENV === "development" && error ? (
          <pre className="AppErrorBoundary_detail">
            {String(error.stack || error.message || error)}
          </pre>
        ) : null}
      </div>
    </div>
  );
}

// Data-router errorElement: reads the thrown error from react-router context.
export function RouteErrorElement() {
  const error = useRouteError();
  console.error("Route render error:", error);
  return <ErrorFallback error={error} />;
}

// Top-level class boundary: catches render errors outside the data router.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
